// Constants
import { MESSAGE_EVENT_TYPE } from './constants/iframe.handlers.constants';
// Enumerations
import { TimerEnum } from '@openbank/ziniaweb-ui-core-models';
// Types
import { IframeBuilderHandlersType, IframeMethodsHandlersType } from './types/iframe.handlers.type';
// Utils
import { iframeIsReadyToBeShown } from './utils/iframe.handlers.utils';

const generateIframeEventListenerEcosystem = (
  onEventListen: IframeBuilderHandlersType['onEventListen']
): (() => void) => {
  const fn = onEventListen as EventListener;

  window.addEventListener(MESSAGE_EVENT_TYPE, fn);
  return () => {
    window.removeEventListener(MESSAGE_EVENT_TYPE, fn);
  };
};

const checkFrameComponentLoadSuccessEventHandler = ({
  setFrameLoaded,
}: Pick<IframeBuilderHandlersType, 'setFrameLoaded'>): void => {
  setFrameLoaded(true);
};

const updateFrameKey = (): ((key: number) => number) => (prevKey: number) => prevKey + 1;

const evaluateIframeComponentHealthyEventHandler = async ({
  frameLoaded,
  errorAttempts,
  onEventListen,
  setFetching,
  setIframeKey,
  setWsRetry,
  webSocketConnected,
}: Omit<IframeBuilderHandlersType, 'setFrameLoaded'>): Promise<any> => {
  setWsRetry(false);
  // Si queremos evaluar la salud del iframe sabiendo que ha habido un error
  if (errorAttempts > 0) {
    window.console.info('Iframe error detected, retrying...');
    setTimeout(() => {
      !frameLoaded && setIframeKey(updateFrameKey());
      !webSocketConnected && setWsRetry(true);
    }, TimerEnum.TIMEOUT_ONE_SECOND);
    // No hay error y el iframe esta listo
  } else if (iframeIsReadyToBeShown({ frameLoaded, webSocketConnected })) {
    window.console.info('Iframe is healthy and ready to be shown');
    setFetching(false);
    return onEventListen && generateIframeEventListenerEcosystem(onEventListen);
  } else {
    window.console.info('Iframe is not ready yet, waiting for it to load');
    window.console.table({ errorAttempts, frameLoaded, webSocketConnected });
    setFetching(true);
    // Si no hay error y el iframe no esta listo, seguimos esperando
  }
};

const removeIframeEventListenerHandler = ({
  onEventListen,
}: Pick<IframeBuilderHandlersType, 'onEventListen'>): void => {
  onEventListen && window.removeEventListener(MESSAGE_EVENT_TYPE, onEventListen as EventListener);
};

const IframeHandlers = ({
  setFrameLoaded,
  ...props
}: IframeBuilderHandlersType): IframeMethodsHandlersType => ({
  handleCheckFrameComponentLoadSuccessEvent: () =>
    checkFrameComponentLoadSuccessEventHandler({ setFrameLoaded }),
  handleEvaluateIframeComponentHealthyEvent: () =>
    evaluateIframeComponentHealthyEventHandler(props),
  handleRemoveIframeEventListener: () => removeIframeEventListenerHandler(props),
});

export { IframeHandlers };
